import React from 'react';
import { find } from 'lodash';

import { SparkleIcon } from '@magoosh/lib/icons';
import AnswerFlag from '@magoosh/organisms/answer_flag';
import { paths } from 'config/path_helpers';
import { toSentence } from 'utilities/formatting';
import { secondsFormattedAsMinutes } from 'utilities/time';

import ContextualLink from '@test_prep/app/components/contextual_link';

import ResultIcon from './result_icon';
import { ExamSectionDetail } from '../types';

import colors from '@magoosh/lib/styles/colors.module.scss';

const AnswersTable: React.FC<ExamSectionDetail> = (props) => {
  const formatSubjectTags = (tags) => {
    if (!tags.length) return null;

    // Some answers have multiple subject tags with a repeated portion. This is especially common
    // for verbal subjects tags where there could be tags like follows on a single prompt:
    // - Sentence Equivalence: Tough Vocabulary, Sentence Equivalence: Psuedo-synonyms
    // This function alters the tags so that the shared "Sentence Equivalence: " portion is only
    // displayed once at the beginning.
    if (tags[0].indexOf(': ') !== -1) {
      const sharedTagBucket =  tags[0].split(': ')[0] + ': ';
      const adjustedTags = tags.map((tag, idx) => {
        if (idx === 0) {
          return tag;
        } else {
          return tag.replace(sharedTagBucket, '');
        }
      });
      return toSentence(adjustedTags);
    } else {
      return toSentence(tags);
    }
  };

  const answerUrl = (answer) => {
    if (answer.shareableId) {
      return paths.publicAnswer(answer.prompt.id, answer.id, answer.shareableId);
    } else {
      return paths.answer(answer.id);
    }
  };

  const renderSectionAnswers = (section) => {
    let questionNumber = 0;

    const rows = section.answers.map((answer, answerIdx) => {
      const scoreReport = find(
        section.scoreReports,
        (scoreReport) => scoreReport.answerIds.indexOf(answer.id) !== -1
      );

      return (
        <tr key={answer.id}>
          <td style={{ paddingLeft: 0, width: '5%' }}>
            <ResultIcon {...answer} />
          </td>
          <td style={{ paddingLeft: 0, width: '30%' }}>
            <div className="d-flex flex-column flex-sm-row gap-xs gap-sm-s">
              <ContextualLink href={answerUrl(answer)} target="_blank" rel="noopener">
                {(() => {
                  if (answer.answerState == 'audio') {
                    return 'Audio Clip';
                  } else if (answer.answerState == 'question_passage') {
                    return 'Passage';
                  } else if (answer.answerState == 'video') {
                    return 'Video Clip';
                  } else {
                    questionNumber += 1;
                    return `Question ${questionNumber}`;
                  }
                })()}
              </ContextualLink>

              {
                scoreReport && (
                  <ContextualLink
                    href={
                      scoreReport.requestType === 'essay' ? (
                        paths.scoreReport(scoreReport.idd)
                      ): (
                        paths.scoreReportAnswer(scoreReport.id, answer.id)
                      )
                    }
                    className="align-items-center d-flex flex-none gap-xxs text-hover-no-decoration text-no-decoration"
                    style={{
                      background: 'linear-gradient(90deg, #0577E9, #5227F8)',
                      borderRadius: 24,
                      boxShadow: '0px 2px 2px -1px #0000000D, 0px 5px 5px -2.5px #0000000D, 0px 0px 0px 1px #0000001A',
                      color: '#fff',
                      padding: '4px 8px'
                    }}
                  >
                    <SparkleIcon color="#fff" size={12} />
                    <div style={{ fontSize: 12 }}>
                      <strong>View Report</strong>
                    </div>
                  </ContextualLink>
                )
              }
            </div>
          </td>
          <td style={{ paddingLeft: 0, width: '40%' }}>{formatSubjectTags(answer.subjectTags)}</td>
          <td style={{ paddingLeft: 0, width: '10%' }}>{answer.difficulty}</td>
          <td style={{ paddingLeft: 0, width: '10%' }}>{secondsFormattedAsMinutes(answer.pace)}</td>
          {
            !answer.shareableId && (
              <td style={{ paddingLeft: 0, width: '5%' }}>
                <AnswerFlag flagged={answer.isFlagged} promptId={answer.prompt.id} />
              </td>
            )
          }
        </tr>
      );
    });

    return <>{rows}</>;
  };

  return (
    <div>
      {
        props.answersBySection.map((section, idx) => (
          <div key={idx}>

            <div className="d-flex">
              <div>
                <h3 className="u-margin-T-s">{section.name}</h3>
              </div>

              <div className="flex-1">
                <h3 className="text-right">
                  {section.isScored && <>{section.percentCorrect}% correct</>}
                </h3>
              </div>
            </div>

            {
              !!section.answers.length ? (
                <div className="table-responsive">
                  <table className="table">
                    <tbody>
                      {renderSectionAnswers(section)}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="u-margin-B-s">You didn't answer any questions in this section.</div>
              )
            }
          </div>
        ))
      }
    </div>
  );
};

export default AnswersTable;
